import { useEffect, useMemo, useRef, useState } from 'react';
import { safeGet } from '@/pages/dashboard/roles/_dashboardShared';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/utils/api';

import { assignBodyForSelection, managerFirstName, type AssignableDealer, type AssignableDealerGroup, type AssignableMember } from './triageHelpers';
import { DashboardRow, VerifySchemaField, FALLBACK_VERIFY_SCHEMA, normalizeVerifyField, INDIAN_STATES, VerifyValues, HotOverdueItem, Tab, Toast, SortKey, LineFilter, FilterState, BLANK_FILTERS, buildTriageFilterParams } from './triageDeskShared';

export function useTriageController() {
const triageProfile = useAuthStore(s => s.user?.role?.name) === 'lead_triage_coordinator';
const currentUserId = useAuthStore((state) => state.user?.id ?? null);
const hscrollRef = useRef<HTMLDivElement | null>(null);
const dragRef = useRef<{ startX: number; startLeft: number; moved: boolean } | null>(null);
const suppressClickRef = useRef(false);
const [tab, setTab] = useState<Tab>('unassigned');
const [search, setSearch] = useState('');
const [rows, setRows] = useState<DashboardRow[]>([]);
const [total, setTotal] = useState(0);
const [loading, setLoading] = useState(true);
const [error, setError] = useState<string | null>(null);
const [toasts, setToasts] = useState<Toast[]>([]);
const [verifyOpenFor, setVerifyOpenFor] = useState<DashboardRow | null>(null);
const [verifyValues, setVerifyValues] = useState<VerifyValues>({});
const [verifySubmitting, setVerifySubmitting] = useState(false);
const [irrelevantLead, setIrrelevantLead] = useState<DashboardRow | null>(null);
const [irrelevantPendingId, setIrrelevantPendingId] = useState<string | null>(null);
const irrelevantOriginQueryRef = useRef<string | null>(null);
const irrelevantOriginDatasetRevisionRef = useRef<number | null>(null);
const dealerNotifiedPendingRef = useRef<Set<string>>(new Set());
const [dealerNotifiedPendingIds, setDealerNotifiedPendingIds] = useState<Set<string>>(new Set());
const [verifySchema, setVerifySchema] = useState<VerifySchemaField[] | null>(null);
const [productOptions, setProductOptions] = useState<string[]>([]);
const [stateOptions, setStateOptions] = useState<string[]>(INDIAN_STATES);
const verifySchemaLoadedRef = useRef(false);
const [nameEdit, setNameEdit] = useState<{ id: string; value: string } | null>(null);
const [nameSaving, setNameSaving] = useState(false);
const [pickerFor, setPickerFor] = useState<DashboardRow | null>(null);
const [pickerMode, setPickerMode] = useState<'assign' | 'reassign'>('assign');
const [members, setMembers] = useState<AssignableMember[] | null>(null);
const [membersLoading, setMembersLoading] = useState(false);
const [dealerGroups, setDealerGroups] = useState<AssignableDealerGroup[] | null>(null);
const [dealersLoading, setDealersLoading] = useState(false);
const [assignBusy, setAssignBusy] = useState(false);
const [filters, setFilters] = useState<FilterState>(BLANK_FILTERS);
const [sortKey, setSortKey] = useState<SortKey>('last_reply_desc');
const [filtersOpen, setFiltersOpen] = useState(false);
const [lineFilter, setLineFilter] = useState<LineFilter>('all');
const [responsesOpenFor, setResponsesOpenFor] = useState<DashboardRow | null>(null);
const [addLeadOpen, setAddLeadOpen] = useState(false);
const [leadsRefresh, setLeadsRefresh] = useState(0);
const [hotOverdueError, setHotOverdueError] = useState<string | null>(null);
const [hotOverdueLoading, setHotOverdueLoading] = useState(true);
const [hotOverdue, setHotOverdue] = useState<HotOverdueItem[]>([]);
const [hotSlaHours, setHotSlaHours] = useState<number | null>(null);
const [hotOverdueRefresh, setHotOverdueRefresh] = useState(0);
useEffect(() => {
    let cancelled = false;
    setHotOverdueLoading(true); setHotOverdueError(null);
    safeGet<{ items: HotOverdueItem[]; slaHours?: number }>('/lead-capture/triage/hot-overdue')
      .then((r) => {
        if (cancelled) return;
        if (!r?.data || !Array.isArray(r.data.items)) throw new Error('Invalid overdue queue');
        if (r.data.items) setHotOverdue(r.data.items);
        if (typeof r.data.slaHours === 'number') setHotSlaHours(r.data.slaHours);
      })
      .catch(() => { if (!cancelled) setHotOverdueError('Could not load overdue leads.'); })
      .finally(() => { if (!cancelled) setHotOverdueLoading(false); });
    return () => { cancelled = true; };
  }, [hotOverdueRefresh, leadsRefresh]);
const membersLoadStartedRef = useRef(false);
useEffect(() => {
    if (!pickerFor || membersLoadStartedRef.current) return;
    membersLoadStartedRef.current = true;
    setMembersLoading(true);
    safeGet<AssignableMember[]>('/leads/triage/assignable-members')
      .then((r) => { if (!Array.isArray(r?.data)) throw new Error('Invalid team roster'); setMembers(r.data); })
      .catch(() => { setMembers([]); membersLoadStartedRef.current = false; setError('Could not load team members. Close and reopen Assign to retry.'); })
      .finally(() => setMembersLoading(false));
  }, [pickerFor]);
const dealersLoadStartedRef = useRef(false);
useEffect(() => {
    if (!pickerFor || dealersLoadStartedRef.current) return;
    dealersLoadStartedRef.current = true;
    setDealersLoading(true);
    fetch('/api/v1/leads/triage/assignable-dealers', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((j) => setDealerGroups(Array.isArray(j?.groups) ? (j.groups as AssignableDealerGroup[]) : []))
      .catch(() => { setDealerGroups([]); dealersLoadStartedRef.current = false; setError('Could not load dealers. Close and reopen Assign to retry.'); })
      .finally(() => setDealersLoading(false));
  }, [pickerFor]);
const [pageSize, setPageSize] = useState(20);
const [offset, setOffset] = useState(0);
const triageQueryIdentity = useMemo(() => {
    const q = buildTriageFilterParams({ tab, search, sortKey, filters, lineFilter });
    q.set('limit', String(pageSize));
    q.set('offset', String(offset));
    return q.toString();
  }, [tab, search, sortKey, filters, lineFilter, pageSize, offset]);
const triageQueryIdentityRef = useRef(triageQueryIdentity);
triageQueryIdentityRef.current = triageQueryIdentity;
const dashboardRequestRevisionRef = useRef(0);
const appliedDatasetRevisionRef = useRef(0);
const rowsRef = useRef(rows);
rowsRef.current = rows;
useEffect(() => {
    if (irrelevantLead && irrelevantOriginQueryRef.current !== triageQueryIdentity) {
      setIrrelevantLead(null);
    }
  }, [triageQueryIdentity, irrelevantLead]);
useEffect(() => { setOffset(0); }, [tab, search, sortKey, filters, lineFilter, pageSize]);
useEffect(() => {
    const requestRevision = ++dashboardRequestRevisionRef.current;
    const requestQuery = triageQueryIdentity;
    if (tab === 'hot_overdue') { setLoading(false); setError(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const q = buildTriageFilterParams({ tab, search, sortKey, filters, lineFilter });
    q.set('limit', String(pageSize));
    q.set('offset', String(offset));
    // Fetch the FULL envelope directly. safeGet() unwraps to res.data.data and
    // DROPS `total` → the pager read total=0 → "0 of 0", Next disabled, and
    // leads past the first page were unreachable. Read total from the envelope.
    fetch(`/api/v1/leads/triage-dashboard?${q.toString()}`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((j) => {
        if (
          cancelled
          || requestRevision !== dashboardRequestRevisionRef.current
          || requestQuery !== triageQueryIdentityRef.current
        ) return;
        if (!Array.isArray(j?.data) || typeof j?.total !== 'number') throw new Error('Invalid triage queue response');
        appliedDatasetRevisionRef.current += 1;
        setRows(j.data);
        setTotal(j.total);
      })
      .catch((e) => {
        if (
          !cancelled
          && requestRevision === dashboardRequestRevisionRef.current
          && requestQuery === triageQueryIdentityRef.current
        ) {
          appliedDatasetRevisionRef.current += 1;
          setRows([]);
          setTotal(0);
          setError(String(e?.message ?? e));
        }
      })
      .finally(() => {
        if (
          !cancelled
          && requestRevision === dashboardRequestRevisionRef.current
          && requestQuery === triageQueryIdentityRef.current
        ) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [tab, search, sortKey, filters, lineFilter, pageSize, offset, leadsRefresh, triageQueryIdentity]);
function openPicker(row: DashboardRow, mode: 'assign' | 'reassign') {
    setPickerFor(row);
    setPickerMode(mode);
  }
async function submitMemberAssign(member: AssignableMember) {
    if (!pickerFor) return;
    const row = pickerFor;
    setAssignBusy(true);
    const prevRows = rows;
    const prevHot = hotOverdue;
    // Unassigned / Overdue-HOT: optimistically drop the row (it leaves the
    // queue once owned). Assigned / Dealer assigned: REASSIGN — update the
    // retained human owner in place on success.
    if (tab === 'unassigned') setRows(rows.filter((x) => x.id !== row.id));
    if (tab === 'hot_overdue') setHotOverdue(hotOverdue.filter((x) => x.id !== row.id));
    try {
      const res = await fetch(`/api/v1/leads/${row.id}/assign-team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(assignBodyForSelection(member)),
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      const who = j?.data?.mode === 'executive'
        ? j.data.assignedToName
        : `${j?.data?.assignedToName ?? 'Manager'}${j?.data?.coordinatorName ? ' + ' + j.data.coordinatorName : ''}`;
      if (tab === 'assigned' || tab === 'dealer_assigned') {
        const d = j?.data ?? {};
        setRows((rs) => rs.map((x) => x.id === row.id
          ? { ...x, assignedToName: d.assignedToName ?? null, coordinatorName: d.coordinatorName ?? null, teamName: d.teamName ?? x.teamName }
          : x));
      }
      const toastId = Math.random().toString(36).slice(2);
      setToasts((t) => [...t, { id: toastId, text: `Assigned to ${who} (${member.teamName})` }]);
      setTimeout(() => setToasts((tt) => tt.filter((x) => x.id !== toastId)), 5000);
      setPickerFor(null);
      // An assignment may clear a HOT-overdue row → refresh the tab count.
      setHotOverdueRefresh((n) => n + 1);
    } catch (e) {
      if (tab === 'unassigned') setRows(prevRows);
      if (tab === 'hot_overdue') setHotOverdue(prevHot);
      setError(`Failed to assign: ${(e as Error).message}`);
    } finally {
      setAssignBusy(false);
    }
  }
function pushToast(text: string) {
    const toastId = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id: toastId, text }]);
    setTimeout(() => setToasts((tt) => tt.filter((x) => x.id !== toastId)), 5000);
  }
async function submitIrrelevant() {
    if (!irrelevantLead || irrelevantPendingId) return;
    const lead = irrelevantLead;
    const originQuery = irrelevantOriginQueryRef.current;
    const originDatasetRevision = irrelevantOriginDatasetRevisionRef.current;
    const currentQuery = triageQueryIdentityRef.current;
    if (
      !originQuery
      || originDatasetRevision === null
      || currentQuery !== originQuery
      || new URLSearchParams(currentQuery).get('tab') !== 'unassigned'
      || appliedDatasetRevisionRef.current !== originDatasetRevision
      || !rowsRef.current.some((row) => row.id === lead.id)
    ) {
      setIrrelevantLead(null);
      irrelevantOriginQueryRef.current = null;
      irrelevantOriginDatasetRevisionRef.current = null;
      return;
    }
    setIrrelevantPendingId(lead.id);
    setError(null);
    try {
      const res = await fetch(`/api/v1/leads/${lead.id}/verify-disposition`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: 'VERIFY_REJECT', rejectReason: 'irrelevant' }),
        credentials: 'include',
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      if (
        triageQueryIdentityRef.current === originQuery
        && appliedDatasetRevisionRef.current === originDatasetRevision
        && rowsRef.current.some((row) => row.id === lead.id)
      ) {
        setRows((current) => current.filter((row) => row.id !== lead.id));
        setTotal((current) => Math.max(0, current - 1));
      }
      setIrrelevantLead(null);
      irrelevantOriginQueryRef.current = null;
      irrelevantOriginDatasetRevisionRef.current = null;
      // Invalidate the server-backed queue even when the user navigated away;
      // returning to Unassigned then reflects the archived result.
      dashboardRequestRevisionRef.current += 1;
      setLeadsRefresh((current) => current + 1);
      pushToast('Marked as irrelevant');
    } catch (e) {
      setError((e as Error).message);
      if (triageQueryIdentityRef.current !== originQuery) {
        irrelevantOriginQueryRef.current = null;
        irrelevantOriginDatasetRevisionRef.current = null;
      }
    } finally {
      setIrrelevantPendingId(null);
    }
  }
async function submitDealerNotified(lead: DashboardRow) {
    if (dealerNotifiedPendingRef.current.has(lead.id) || lead.dealerNotifiedAt) return;
    const originQuery = triageQueryIdentityRef.current;
    const originDatasetRevision = appliedDatasetRevisionRef.current;
    const originRow = rowsRef.current.find((row) => row.id === lead.id);
    const originDealerId = originRow?.assignedDealerId;
    if (
      new URLSearchParams(originQuery).get('tab') !== 'dealer_assigned'
      || !originRow
      || typeof originDealerId !== 'string'
      || originDealerId.length === 0
    ) return;

    dealerNotifiedPendingRef.current.add(lead.id);
    setDealerNotifiedPendingIds(new Set(dealerNotifiedPendingRef.current));
    setError(null);
    try {
      // Manual acknowledgement only: no dealer/body payload and no message
      // transport. The server resolves the current assignment atomically.
      const res = await fetch(`/api/v1/leads/${lead.id}/dealer-notified`, {
        method: 'POST',
        credentials: 'include',
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);

      const notifiedAt = typeof json?.data?.dealerNotifiedAt === 'string'
        ? json.data.dealerNotifiedAt
        : null;
      const actorId = typeof json?.data?.dealerNotifiedByUserId === 'string'
        ? json.data.dealerNotifiedByUserId
        : null;
      const acknowledgedDealerId = typeof json?.data?.assignedDealerId === 'string'
        ? json.data.assignedDealerId
        : null;
      if (!notifiedAt) throw new Error('The acknowledgement response did not include a sent time.');

      const currentRow = rowsRef.current.find((row) => row.id === lead.id);
      const sameDataset = triageQueryIdentityRef.current === originQuery
        && appliedDatasetRevisionRef.current === originDatasetRevision
        && currentRow?.assignedDealerId === originDealerId;
      if (sameDataset && acknowledgedDealerId !== originDealerId) {
        // The server acknowledged the assignment that exists now, but this
        // screen displayed another dealer when the click began. Never attach
        // that receipt to the stale dealer: reload this exact query instead.
        dashboardRequestRevisionRef.current += 1;
        setLeadsRefresh((current) => current + 1);
        pushToast('Dealer assignment changed; refreshing the lead');
      } else if (sameDataset) {
        const actorName = typeof json?.data?.dealerNotifiedByName === 'string'
          && json.data.dealerNotifiedByName.trim()
          ? json.data.dealerNotifiedByName.trim()
          : actorId !== null && actorId === currentUserId
            ? 'you'
            : null;
        setRows((current) => current.map((row) => row.id === lead.id
          ? {
              ...row,
              dealerNotifiedAt: notifiedAt,
              dealerNotifiedByUserId: actorId,
              // Idempotent success can belong to another operator. Only call
              // it "you" when the returned actor is the authenticated user.
              dealerNotifiedByName: actorName,
            }
          : row));
        pushToast('Dealer notification marked as sent');
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      dealerNotifiedPendingRef.current.delete(lead.id);
      setDealerNotifiedPendingIds(new Set(dealerNotifiedPendingRef.current));
    }
  }
async function submitDealerAssign(dealer: AssignableDealer, group: AssignableDealerGroup) {
    if (!pickerFor) return;
    const row = pickerFor;
    setAssignBusy(true);
    try {
      const res = await fetch(`/api/v1/leads/${row.id}/assign-dealer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dealerId: dealer.id }),
        credentials: 'include',
      });
      const j = await res.json().catch(() => ({}));
      if (res.status === 422 && j?.error === 'NO_EXEC_OWNER') {
        pushToast('Assign an executive owner first, then the dealer');
        return;
      }
      if (!res.ok) throw new Error(j?.message || j?.error || `HTTP ${res.status}`);
      const name: string = j?.data?.assignedDealer?.name ?? dealer.name;
      const dealerChanged = row.assignedDealerId !== dealer.id;
      const patch = <T extends { id: string }>(x: T): T => (x.id === row.id ? {
        ...x,
        assignedDealerId: j?.data?.assignedDealer?.id ?? dealer.id,
        assignedDealerName: name,
        assignedDealerManagerName: group.managerName,
        dealerAssignedAt: j?.data?.dealerAssignedAt ?? null,
        // A changed dealer is a new handoff. Mirror the server's atomic reset
        // immediately while retaining the independent human owner fields. An
        // A→A assignment is a no-op and must retain its durable sent receipt.
        dealerNotifiedAt: dealerChanged ? null : row.dealerNotifiedAt ?? null,
        dealerNotifiedByUserId: dealerChanged ? null : row.dealerNotifiedByUserId ?? null,
        dealerNotifiedByName: dealerChanged ? null : row.dealerNotifiedByName ?? null,
      } : x);
      setRows((rs) => rs.map(patch));
      setHotOverdue((hs) => hs.map(patch));
      const owner = group.managerUserId ? ` (${managerFirstName(group.managerName)}'s)` : '';
      pushToast(`Assigned to dealer ${name}${owner}`);
      setPickerFor(null);
    } catch (e) {
      setError(`Failed to assign dealer: ${(e as Error).message}`);
    } finally {
      setAssignBusy(false);
    }
  }
async function createDealerFromPicker(input: {
    name: string; phone: string; pincode: string; gstin: string | null; managerUserId: string;
  }): Promise<AssignableDealer> {
    const res = await api.post('/dealers', input);
    const d = res?.data?.dealer ?? res?.data?.data ?? {};
    return { id: String(d.id), name: d.name ?? input.name, city: d.city ?? null, phone: d.phone ?? input.phone, openLeads: 0 };
  }
useEffect(() => {
    // CVG4-TRIAGE-1 (L117): the add-lead modal reuses the same product-options
    // source, so opening it also triggers this lazy load.
    if ((!verifyOpenFor && !addLeadOpen) || verifySchemaLoadedRef.current) return;
    verifySchemaLoadedRef.current = true;
    let cancelled = false;

    (async () => {
      // 1. C-AVIKAFORM schema (either mount BE-CORE lands it under). qa4e:
      // backend fields use `input` + catalog/gazetteer sources and include
      // derived read-only entries — normalizeVerifyField maps/drops them.
      let schemaLoaded = false;
      for (const url of ['/api/v1/leads/triage/verify-schema', '/api/v1/triage/verify-schema']) {
        try {
          const res = await fetch(url, { credentials: 'include' });
          if (!res.ok) continue;
          const j = await res.json();
          const raw = j?.data?.fields ?? j?.data;
          if (Array.isArray(raw) && raw.length > 0 && raw.every((f: any) => f && typeof f.key === 'string')) {
            const fields = raw.map(normalizeVerifyField).filter((f): f is VerifySchemaField => f !== null);
            if (cancelled) return;
            if (fields.length > 0) {
              setVerifySchema(fields);
              const src = j?.data?.sources;
              if (Array.isArray(src?.products) && src.products.length) setProductOptions(src.products.map(String));
              if (Array.isArray(src?.locations) && src.locations.length) setStateOptions(src.locations.map(String));
              schemaLoaded = true;
              break;
            }
          }
        } catch { /* try next / fall through */ }
      }
      if (cancelled) return;
      if (!schemaLoaded) setVerifySchema(FALLBACK_VERIFY_SCHEMA);
      // 2. Product options from the catalog (same source ProductGridSelector
      // uses) — needed on BOTH paths (the server schema ships a catalog URL,
      // not inline options).
      try {
        const res = await fetch('/api/v1/products/categories', { credentials: 'include' });
        if (res.ok) {
          const j = await res.json();
          const cats = (j?.data ?? j?.categories ?? [])
            .map((c: any) => (typeof c === 'string' ? c : c?.name ?? c?.category))
            .filter((c: any): c is string => typeof c === 'string' && c.trim().length > 0);
          if (!cancelled && cats.length > 0) setProductOptions((prev) => (prev.length > 0 ? prev : cats));
        }
      } catch { /* product field degrades to free-text */ }
    })();

    return () => { cancelled = true; };
  }, [verifyOpenFor, addLeadOpen]);
function openVerify(r: DashboardRow) {
    const schema = verifySchema ?? FALLBACK_VERIFY_SCHEMA;
    const raw: Record<string, string> = {
      name: r.name ?? '',
      product: r.requirement ?? '', // the Requirement column holds the product label
      applicationType: r.applicationType ?? '',
      leadCapacity: r.capacity ?? '',
      leadTimeline: r.timeline ?? '',
      indicativePriceShared: r.priceShared ? 'Yes' : '',
      indicativePriceRange: r.priceRange ?? '',
      location: r.location ?? '',
      locationState: r.state ?? '',
    };
    const values: VerifyValues = {};
    for (const f of schema) {
      const val = (raw[f.key] ?? '').trim();
      if (!val) continue;
      if (f.type === 'select' && !f.source && f.options && f.options.length > 0 && !f.options.includes(val)) {
        continue; // out-of-options legacy value → drop, never show/submit
      }
      values[f.key] = val;
    }
    setVerifyOpenFor(r);
    setVerifyValues(values);
  }
function optionsForField(f: VerifySchemaField): string[] {
    if (f.options && f.options.length > 0) return f.options;
    if (f.source === 'products') return productOptions;
    if (f.source === 'locations') return stateOptions;
    return [];
  }
async function saveName() {
    if (!nameEdit) return;
    const { id, value } = nameEdit;
    const name = value.trim();
    if (!name) { setNameEdit(null); return; }
    setNameSaving(true);
    try {
      const res = await fetch(`/api/v1/leads/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows((prev) => prev.map((row) => (row.id === id ? { ...row, name } : row)));
      const toastId = Math.random().toString(36).slice(2);
      setToasts((t) => [...t, { id: toastId, text: `Name updated to "${name}"` }]);
      setTimeout(() => setToasts((tt) => tt.filter((x) => x.id !== toastId)), 4000);
      setNameEdit(null);
    } catch (e) {
      setError(`Name update failed: ${(e as Error).message}`);
    } finally {
      setNameSaving(false);
    }
  }
async function submitVerify() {
    if (!verifyOpenFor) return;
    setVerifySubmitting(true);
    try {
      const schema = verifySchema ?? FALLBACK_VERIFY_SCHEMA;
      const payload: Record<string, unknown> = {};
      for (const f of schema) {
        // Hidden conditional fields (e.g. b2b* when application ≠ B2B) are not submitted.
        if (f.showWhen && (verifyValues[f.showWhen.key] ?? '').trim() !== f.showWhen.equals) continue;
        const val = (verifyValues[f.key] ?? '').trim();
        if (!val) continue;
        if (f.type === 'boolean') payload[f.key] = val === 'Yes';
        else if (f.type === 'select' && !f.source && f.options && f.options.length > 0 && !f.options.includes(val)) continue;
        else payload[f.key] = val;
      }
      const res = await fetch(`/api/v1/leads/${verifyOpenFor.id}/verify-manually`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const verifiedByName = json?.data?.verifiedByName ?? null;
      const freshScore = json?.data?.score;
      const freshTier = json?.data?.interestLevel;
      const derivedSegment = json?.data?.businessSegment
        ?? (payload.applicationType ? (payload.applicationType === 'B2B' ? 'B2B' : 'B2C') : null);

      // Update the row inline (businessSegment comes DERIVED from the server).
      setRows((prev) => prev.map((row) => row.id === verifyOpenFor.id
        ? {
            ...row,
            ...(payload.name            ? { name: String(payload.name) } : {}),
            ...(payload.location        ? { location: String(payload.location) } : {}),
            ...(payload.locationState   ? { state: String(payload.locationState) } : {}),
            ...(payload.product         ? { requirement: String(payload.product) } : {}),
            ...(payload.applicationType ? { applicationType: String(payload.applicationType) } : {}),
            ...(derivedSegment          ? { businessSegment: derivedSegment } : {}),
            ...(payload.leadCapacity    ? { capacity: String(payload.leadCapacity) } : {}),
            ...(payload.leadTimeline    ? { timeline: String(payload.leadTimeline) } : {}),
            ...(payload.indicativePriceShared === true ? { priceShared: true } : {}),
            ...(payload.indicativePriceRange ? { priceRange: String(payload.indicativePriceRange) } : {}),
            ...(freshScore !== undefined && freshScore !== null ? { score: freshScore } : {}),
            ...(freshTier ? { tier: freshTier } : {}),
            verifiedStatus: 'verified',
            verifiedBy: 'manual',
            verifiedByName,
          }
        : row));
      const toastId = Math.random().toString(36).slice(2);
      setToasts((t) => [...t, { id: toastId, text: `Verified ${verifyOpenFor.name ?? verifyOpenFor.phone ?? 'lead'}` }]);
      setTimeout(() => setToasts((tt) => tt.filter((x) => x.id !== toastId)), 4000);
      setVerifyOpenFor(null);
      setVerifyValues({});
    } catch (e) {
      setError(`Verify failed: ${(e as Error).message}`);
    } finally {
      setVerifySubmitting(false);
    }
  }
const overdueRows: DashboardRow[] = useMemo(
    () => hotOverdue.map((h) => ({
      id: h.id,
      createdAt: h.hotSince,
      enquiryDate: h.hotSince.slice(0, 10),
      source: null,
      name: h.name,
      phone: h.phone,
      email: null,
      location: h.location,
      requirement: h.product,
      applicationType: null,
      businessSegment: null,
      verifiedStatus: 'unverified' as const,
      verifiedBy: null,
      verifiedByName: null,
      assignedToName: null,
      teamName: null,
      assignedDealerName: h.assignedDealerName ?? null,
      score: h.score,
      tier: 'HOT' as const,
      scoringModel: null,
      capacity: null,
      capacitySource: null,
      quantity: null,
      timeline: null,
      priceShared: false,
      priceRange: null,
      state: null,
      waLineLabel: null,
      isB2bPriority: false,
      isOutOfBudget: false,
    })),
    [hotOverdue],
  );
const displayRows = tab === 'hot_overdue' ? overdueRows : rows;
const shownTotal = tab === 'hot_overdue' ? hotOverdue.length : total;
return { hotOverdueError, hotOverdueLoading, triageProfile, currentUserId, hscrollRef, dragRef, suppressClickRef, tab, setTab, search, setSearch, rows, setRows, total, setTotal, loading, setLoading, error, setError, toasts, setToasts, verifyOpenFor, setVerifyOpenFor, verifyValues, setVerifyValues, verifySubmitting, setVerifySubmitting, irrelevantLead, setIrrelevantLead, irrelevantPendingId, setIrrelevantPendingId, irrelevantOriginQueryRef, irrelevantOriginDatasetRevisionRef, dealerNotifiedPendingRef, dealerNotifiedPendingIds, setDealerNotifiedPendingIds, verifySchema, setVerifySchema, productOptions, setProductOptions, stateOptions, setStateOptions, verifySchemaLoadedRef, nameEdit, setNameEdit, nameSaving, setNameSaving, pickerFor, setPickerFor, pickerMode, setPickerMode, members, setMembers, membersLoading, setMembersLoading, dealerGroups, setDealerGroups, dealersLoading, setDealersLoading, assignBusy, setAssignBusy, filters, setFilters, sortKey, setSortKey, filtersOpen, setFiltersOpen, lineFilter, setLineFilter, responsesOpenFor, setResponsesOpenFor, addLeadOpen, setAddLeadOpen, leadsRefresh, setLeadsRefresh, hotOverdue, setHotOverdue, hotSlaHours, setHotSlaHours, hotOverdueRefresh, setHotOverdueRefresh, membersLoadStartedRef, dealersLoadStartedRef, pageSize, setPageSize, offset, setOffset, triageQueryIdentity, triageQueryIdentityRef, dashboardRequestRevisionRef, appliedDatasetRevisionRef, rowsRef, openPicker, submitMemberAssign, pushToast, submitIrrelevant, submitDealerNotified, submitDealerAssign, createDealerFromPicker, openVerify, optionsForField, saveName, submitVerify, overdueRows, displayRows, shownTotal };
}
